import _ from "lodash";
import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Form, Grid, Loader, Segment } from "semantic-ui-react";
import { useAuth0 } from "../../../../auth/auth0";
import { useUserAuth } from "../../../../hooks/useUserAuth";

import { useQuery } from "@tanstack/react-query";
import { getProtocolAnalysisResultsForPianoChartsAsync } from "../../../../apis/analysisApi";
import type { ProtocolPlotAnalysisResult } from "../../../../apis/types/analysisTypes";
import type { ProtocolTreatment } from "../../../../apis/types/protocolTypes";
import { useModuleNavigation } from "../../../../hooks/useModuleNavigation";
import ProtocolAnalysisTopBar from "../../../Lumber/ProtocolAnalysisTopBar";
import PianoChart from "./PianoChart";
import PianoChartLocationSelector from "./PianoChartLocationSelector";
import { useProtocolAnalysis } from "./ProtocolAnalysisContext";
import { useProtocolDataQuery } from "./queries";
import type {
	PianoLocation,
	PianoSelectedAverages,
	PianoSiteRow,
	PianoTrialAverage,
	ProtocolPianoChartsProps,
} from "./types";
import { getAssessmentOptions, getDataNormalizationOptions } from "./utils";
import styles from "./ProtocolPianoCharts.module.css";

const ProtocolPianoCharts = ({ dashboardOptions, standCountAnalyses, treatmentData }: ProtocolPianoChartsProps) => {
	const userAuth = useUserAuth();
	const { getTokenSilently } = useAuth0();
	const moduleNavigation = useModuleNavigation();

	const { committed, pending, display, isDirty } = useProtocolAnalysis();

	//TODO: This is here till we decide to move normalization to backend or not
	const dataNormalizationOptions = useMemo(
		() =>
			dashboardOptions
				? getDataNormalizationOptions(dashboardOptions.dataNormalizations, treatmentData, {
						excludeNonTreatmentTypes: true,
					})
				: [],
		[dashboardOptions, treatmentData],
	);

	//TODO: Can probably move this to display settings in context
	const hasAssessments = useMemo(
		() => (dashboardOptions ? _.some(getAssessmentOptions(dashboardOptions), (o) => o.kind !== "standcount") : false),
		[dashboardOptions],
	);

	//UI Control
	//FIXME: this is by trial id not by location!
	const [uncheckedTrialIds, setUncheckedTrialIds] = useState(() => new Set<string>());

	//Data Sources
	//index.tsx only mounts this page once the protocol has loaded, so this reads the cached copy
	const { data: protocolDataResponse } = useProtocolDataQuery({ refetchOnMount: false });
	const protocolData = protocolDataResponse?.protocolData ?? null;
	const protocolName = useSelector((state) => state.entityNames?.protocolName ?? "");

	const isGroundData = pending.assessmentKind === "grounddata";
	const isCurveModel = pending.assessmentKind === "curvemodel";

	const { data: rawTrialData, isFetching: loading } = useQuery({
		// TODO(types): removeOutliers and outlierType are ignored by the server but still refetch on change.
		queryKey: [
			"protocol piano chart data",
			userAuth.currentClientId,
			moduleNavigation.protocolId,
			pending.assessmentValue,
			pending.plannedTimingId,
			committed.removeOutliers,
			committed.removeDamagedOrExcluded,
			committed.outlierType,
		],
		queryFn: async () => {
			const accessToken = await getTokenSilently();

			//The endpoint takes "" rather than null for the ids that do not apply to the
			//selected assessment kind.
			//The casts drop nulls the types can't rule out: `enabled` requires the client and protocol
			//ids, and the context sets the ids of the selected assessment kind.
			return getProtocolAnalysisResultsForPianoChartsAsync(
				userAuth.currentClientId as string,
				moduleNavigation.protocolId as string,
				accessToken,
				isGroundData ? (pending.groundDataAssessmentId as string) : "",
				isGroundData ? (pending.plannedTimingId as string) : "",
				isCurveModel ? (pending.curveModelAnalysisId as string) : "",
				isCurveModel ? (pending.analysisId as string) : "",
				false,
				isCurveModel ? (pending.analysisTypeId as string) : "",
				false,
				committed.removeOutliers,
				committed.removeDamagedOrExcluded,
				committed.outlierType,
			);
		},
		select: (data) => {
			const grouped = _.values(_.groupBy(data.plotAnalysisResults, "trialId"));

			const filteredTreatments: ProtocolPlotAnalysisResult[][] = [];
			_.filter(grouped, (r) => {
				const f = _.filter(r, (trial) => {
					// TODO(types): protocolData is unguarded here (index.tsx doesn't mount the page while it is
					// null), and a null trialTreatmentId is coerced to 0 by `<=`. The cast keeps that coercion.
					return (
						(trial.trialTreatmentId as number) <= protocolData!.treatments && trial.excludeFromAssessment === false
					);
				});
				if (f && f.length > 0) {
					filteredTreatments.push(f);
				}
			});

			return filteredTreatments;
		},
		enabled: !!pending.assessmentValue && !!userAuth.currentClientId && !!moduleNavigation.protocolId,
	});

	const siteSpecificData = useMemo(() => {
		if (!rawTrialData || !protocolData || !pending.dataNormalizationId) return [];
		let siteData: PianoSiteRow[] = [];
		_.map(rawTrialData, (td) => {
			const trialData: PianoSiteRow[] = [];
			for (let i = 1; i <= protocolData.treatments; ++i) {
				// TODO(types): a row passes the filter when either value is non-null, so the picked
				// value can still be null; _.meanBy then sums it as 0.
				let trialTreatmentData = _.map(
					_.filter(td, (trial) => {
						return (
							trial.trialTreatmentId === i &&
							trial.excluded !== true &&
							(trial.groundDatasetAssessmentValue !== null || trial.curveModelAnalysisValue !== null)
						);
					}),
					isGroundData ? "groundDatasetAssessmentValue" : "curveModelAnalysisValue",
				);
				if (td?.length > 0) {
					const siteObject: PianoSiteRow = {
						trialId: td[0].trialId,
						trialName: td[0].trialName,
						trialTreatmentId: i,
						average: 0,
						excluded: true,
						region: td[0].region,
						countryCode: td[0].countryCode,
						city: td[0].city,
						assessmentName: display.assessmentName ?? "",
						treatmentName: _.find(td, (t) => {
							return t.trialTreatmentId === i;
						})?.treatmentName,
					};

					if (trialTreatmentData.length > 0) {
						trialTreatmentData = _.map(trialTreatmentData, (data) => {
							return data;
						});
						const average = _.meanBy(trialTreatmentData);
						siteObject.average = average;

						siteObject.excluded = false;
					}

					trialData.push(siteObject);
				}
			}
			siteData = siteData.concat(trialData);
		});
		return siteData;
	}, [rawTrialData, protocolData, pending.dataNormalizationId, isGroundData, display.assessmentName]);

	const groupedSiteSpecificDataByTrial = useMemo(
		() => _.values(_.groupBy(siteSpecificData, "trialTreatmentId")),
		[siteSpecificData],
	);

	const locations = useMemo(() => {
		const uniqTrials = _.uniqBy(siteSpecificData, "trialId");
		const locs = _.map(uniqTrials, (ut): PianoLocation => {
			return {
				trialId: ut.trialId,
				city: ut.city,
				region: ut.region,
				countryCode: ut.countryCode,
				checked: !uncheckedTrialIds.has(ut.trialId),
			};
		});
		return locs;
	}, [siteSpecificData, uncheckedTrialIds]);

	const selectedAverageTreatments = useMemo((): PianoSelectedAverages => {
		const normalization = _.find(dataNormalizationOptions, ["value", pending.dataNormalizationId]);
		const currentTreatment = _.find(treatmentData, {
			treatmentTypeId: normalization?.treatmentTypeId,
		} as Partial<ProtocolTreatment>);

		if (currentTreatment) {
			return _.filter(siteSpecificData, { trialTreatmentId: currentTreatment.trialTreatmentId });
		} else if (normalization?.text === "% MEAN") {
			return _.map(_.groupBy(siteSpecificData, "trialId"), (g): PianoTrialAverage => {
				const averages = _.map(g, "average");
				const average = _.meanBy(averages);
				return { trialId: g[0].trialId, average: average };
			});
		}
		// undefined case should never happen as switching tabs will update dropdown to a valid result
		return undefined;
	}, [dataNormalizationOptions, pending.dataNormalizationId, siteSpecificData, treatmentData]);

	const filteredGroupedSiteSpecificDataByTrial = useMemo(() => {
		const averageByTrial = _.keyBy<PianoSiteRow | PianoTrialAverage>(selectedAverageTreatments, "trialId");
		return groupedSiteSpecificDataByTrial.map((group) =>
			group
				.filter((s) => !uncheckedTrialIds.has(s.trialId))
				.map((s) => ({
					...s,
					// TODO(types): a trial missing from averageByTrial divides by undefined, giving NaN.
					average: parseFloat(((s.average / averageByTrial[s.trialId]?.average) * 100 - 100).toFixed(2)),
				})),
		);
	}, [groupedSiteSpecificDataByTrial, selectedAverageTreatments, uncheckedTrialIds]);

	const { minimumAverage, maximumAverage } = useMemo(() => {
		const all = _.flatten(filteredGroupedSiteSpecificDataByTrial).map((s) => s.average);
		// TODO(types): null when no chart has rows; PianoChart takes number | null | undefined.
		return all.length
			? { minimumAverage: _.min(all), maximumAverage: _.max(all) }
			: { minimumAverage: null, maximumAverage: null };
	}, [filteredGroupedSiteSpecificDataByTrial]);

	function updateSelectedLocation(trialId: string, checked: boolean) {
		setUncheckedTrialIds((prev) => {
			const next = new Set(prev);
			if (checked) {
				next.delete(trialId);
			} else {
				next.add(trialId);
			}
			return next;
		});
	}

	return !hasAssessments ? (
		<Segment style={{ width: "100%" }}>
			<p>{"We could not find data for this protocol."}</p>
		</Segment>
	) : (
		<>
			<ProtocolAnalysisTopBar
				mode="piano"
				dashboardOptions={dashboardOptions}
				standCountAnalyses={standCountAnalyses}
				treatments={treatmentData}
				loading={loading}
			/>
			<Segment basic>
				<Form>
					{loading ? (
						<Loader active inline="centered" />
					) : (
						<>
							<Grid stackable>
								<Grid.Column mobile={16} tablet={4} computer={2}>
									{locations && locations?.length > 0 ? (
										<PianoChartLocationSelector locations={locations} updateSelectedLocation={updateSelectedLocation} />
									) : null}
								</Grid.Column>
								<Grid.Column mobile={16} tablet={12} computer={14}>
									<div className={styles.pianoChartsGrid}>
										{_.map(filteredGroupedSiteSpecificDataByTrial, (c, chartIndex) => {
											const fullSiteData = groupedSiteSpecificDataByTrial[chartIndex] ?? c;
											return (
												<div
													className={`${styles.pianoChartsGridItem} ${styles.pianoChartsGridItemMedium}`}
													key={fullSiteData[0]?.trialTreatmentId ?? chartIndex}
												>
													<PianoChart
														siteData={c}
														selectedAverageTreatments={selectedAverageTreatments}
														trialTreatmentId={fullSiteData[0]?.trialTreatmentId}
														treatmentName={fullSiteData[0]?.treatmentName}
														minimumAverage={minimumAverage}
														maximumAverage={maximumAverage}
														needToUpdateAnalytics={isDirty}
														layoutLocationCount={fullSiteData.length}
														featureName={protocolName}
													/>
												</div>
											);
										})}
									</div>
								</Grid.Column>
							</Grid>
						</>
					)}
				</Form>
			</Segment>
		</>
	);
};

export default ProtocolPianoCharts;
